import { useMemo } from "react";

import { cn } from "@/lib/utils";

import {
  TREE_INDENT_PER_LEVEL,
  TreeNode
} from "../adminAreaTreeSelection/TreeNode";

// Generic types for tree data
export interface TreeNode<T> {
  id: string;
  label: string;
  children?: T[];
}

export interface TreeSelectionProps<TParent, TChild> {
  /** Array of parent nodes to display */
  data: TParent[];
  /** Set of expanded parent node IDs */
  expandedNodes: Set<string>;
  /** Toggle expand/collapse for a parent node */
  onToggleExpand: (nodeId: string) => void;
  /** Map of selected items by parent node ID */
  selectedItems: Record<string, TChild[]>;
  /** Handler for selecting all children in a parent node */
  onSelectAll: (parent: TParent, checked: boolean) => void;
  /** Handler for selecting a single child item */
  onSelectItem: (parent: TParent, item: TChild, checked: boolean) => void;
  /** Function to get unique key for parent */
  getParentKey: (parent: TParent) => string;
  /** Function to get unique key for child */
  getChildKey: (child: TChild) => string;
  /** Optional search query to filter children */
  searchQuery?: string;
  /** Optional function to get children from parent (defaults to parent.children) */
  getChildren: (parent: TParent) => TChild[];
  /** Optional function to get label from parent (defaults to parent.label) */
  getParentLabel: (parent: TParent) => string;
  /** Optional function to get label from child (defaults to child.name) */
  getChildLabel: (child: TChild) => string;
  /** Optional title for the tree selection area */
  title?: string;
  /** Optional className for the container */
  className?: string;
  /** Indentation in pixels per depth level */
  indentationPx?: number;
  /** Function to determine if a node has children */
  hasChildren?: (node: TParent | TChild, parent?: TParent) => boolean;
  /** Optional loading state to show spinner on nodes */
  isLoading?: { [nodeId: string]: boolean };
  /** Optional placeholder to show when there are no items */
  emptyPlaceholder?: React.ReactNode;
}

function TreeNodeItem<TParent, TChild>({
  node,
  depth,
  parent,
  expandedNodes,
  selectedItems,
  searchQuery,
  onToggleExpand,
  onSelectAll,
  onSelectItem,
  getChildren,
  getParentLabel,
  getChildLabel,
  getParentKey,
  getChildKey,
  hasChildren: hasChildrenProp,
  isLoading
}: {
  node: TParent | TChild;
  depth: number;
  parent?: TParent;
} & Pick<
  TreeSelectionProps<TParent, TChild>,
  | "expandedNodes"
  | "onToggleExpand"
  | "selectedItems"
  | "onSelectAll"
  | "onSelectItem"
  | "searchQuery"
  | "getChildren"
  | "getParentLabel"
  | "getChildLabel"
  | "getParentKey"
  | "getChildKey"
  | "hasChildren"
  | "isLoading"
>) {
  const isParent = !parent;
  const key = isParent
    ? getParentKey(node as TParent)
    : getChildKey(node as TChild);
  const name = isParent
    ? getParentLabel?.(node as TParent)
    : getChildLabel?.(node as TChild);

  const children = useMemo(() => {
    if (!isParent) return [];
    return getChildren?.(node as TParent) ?? [];
  }, [isParent, getChildren, node]);
  const hasChildren =
    typeof hasChildrenProp === "function"
      ? hasChildrenProp(node, parent)
      : !!(children && children.length > 0);

  const isExpanded = expandedNodes.has(key);

  // Filter children by search query
  const filteredChildren = useMemo(() => {
    if (!searchQuery || !isParent) return children;
    return children?.filter((child) =>
      (getChildLabel?.(child) ?? "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [children, isParent, searchQuery, getChildLabel]);

  const selectedChildren = selectedItems[key] || [];
  const parentKey = parent ? getParentKey(parent) : "";
  const isSelected = isParent
    ? selectedChildren.length > 0 && selectedChildren.length >= children?.length
    : selectedItems[parentKey]?.some((c) => getChildKey(c) === key);

  const isIndeterminate = isParent
    ? selectedChildren.length > 0 &&
      selectedChildren.length < (children ?? []).length
    : false;

  const handleToggleSelect = (checked: boolean) => {
    if (isParent) {
      onSelectAll(node as TParent, checked);
    } else if (parent) {
      onSelectItem(parent, node as TChild, checked);
    }
  };

  if (!key || name === undefined) {
    return null;
  }

  return (
    <div key={key}>
      <TreeNode
        node={{ id: key, name }}
        depth={depth}
        isExpanded={isExpanded}
        isSelected={!!isSelected}
        isIndeterminate={isIndeterminate}
        hasChildren={hasChildren}
        onToggleExpand={() => onToggleExpand(key)}
        onToggleSelect={handleToggleSelect}
        isLoading={isLoading ? isLoading[key] : false}
        showCheckbox={!isParent || hasChildren}
        labelClassName="text-sm"
      />
      {isExpanded && isLoading?.[key] && (
        <div
          className="ml-4"
          style={{ paddingLeft: (depth + 1) * TREE_INDENT_PER_LEVEL + 8 }}
        >
          <span className="text-xs text-txtBody">
            The data still loading from the server...
          </span>
        </div>
      )}
      {isExpanded && hasChildren && (
        <div>
          {filteredChildren?.map((child) => (
            <TreeNodeItem
              key={getChildKey(child)}
              node={child}
              depth={depth + 1}
              parent={node as TParent}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              selectedItems={selectedItems}
              onSelectAll={onSelectAll}
              onSelectItem={onSelectItem}
              searchQuery={searchQuery}
              getChildren={getChildren}
              getParentLabel={getParentLabel}
              getChildLabel={getChildLabel}
              getParentKey={getParentKey}
              getChildKey={getChildKey}
              hasChildren={hasChildrenProp}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A reusable tree selection component that displays parent-child hierarchical data
 * with expand/collapse functionality and multi-select checkboxes.
 */
export function TreeSelection<TParent, TChild>({
  data,
  expandedNodes,
  onToggleExpand,
  selectedItems,
  onSelectAll,
  onSelectItem,
  searchQuery = "",
  getChildren,
  getParentLabel,
  getChildLabel,
  getParentKey,
  getChildKey,
  title,
  className,
  hasChildren,
  isLoading,
  emptyPlaceholder
}: Readonly<TreeSelectionProps<TParent, TChild>>) {
  const renderProps = {
    expandedNodes,
    onToggleExpand,
    selectedItems,
    onSelectAll,
    onSelectItem,
    searchQuery,
    getChildren,
    getParentLabel,
    getChildLabel,
    getParentKey,
    getChildKey,
    hasChildren,
    isLoading,
    emptyPlaceholder
  };

  return (
    <div
      className={cn("flex-1 overflow-y-auto p-3 custom-scrollbar", className)}
    >
      {title && (
        <h4 className="text-sm font-semibold mb-2 text-txtHeading">{title}</h4>
      )}
      {data.length === 0 ? (
        emptyPlaceholder
      ) : (
        <div className="flex flex-col">
          {data.map((parent) => (
            <TreeNodeItem
              key={getParentKey(parent)}
              node={parent}
              depth={0}
              {...renderProps}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TreeSelection;
